import { useState, useEffect } from "react";
import { toast } from "sonner";
import { XIcon, SpinnerGapIcon, EnvelopeSimpleIcon, LockKeyIcon, StarIcon } from "@phosphor-icons/react";
import { useAuth } from "../context/AuthContext";

function errMsg(e) {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x.msg || "").join(" ");
  return "Something went wrong — try again";
}

export const AuthModal = () => {
  const { modal, closeAuth, login, register, verify, resendCode } = useAuth();
  const [tab, setTab] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (modal.open) {
      setTab(modal.tab || "login");
      if (modal.email) setEmail(modal.email);
      setCode("");
    }
  }, [modal.open, modal.tab, modal.email]);

  if (!modal.open) return null;

  const submit = async () => {
    setBusy(true);
    try {
      if (tab === "verify") {
        await verify(email, code);
      } else if (tab === "signup") {
        const res = await register(email.trim(), password);
        if (res.needsVerification) { toast.success("Code sent — check your email"); setTab("verify"); }
      } else {
        await login(email.trim(), password);
      }
    } catch (e) {
      toast.error(errMsg(e));
    }
    setBusy(false);
  };

  const isVerify = tab === "verify";

  return (
    <div data-testid="auth-modal" className="fixed inset-0 z-[130] flex items-center justify-center bg-black/85 backdrop-blur-sm p-4" onClick={closeAuth}>
      <div className="bg-card border border-neon/25 max-w-sm w-full p-6 space-y-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <div className="font-heading font-black text-xl tracking-tighter">ALPHA<span className="text-neon">APEING</span></div>
          <button data-testid="auth-close-btn" onClick={closeAuth} className="p-1 text-muted-foreground hover:text-white"><XIcon size={20} /></button>
        </div>

        {!isVerify && (
          <>
            {modal.reason && (
              <div data-testid="auth-nudge" className="flex items-start gap-2 bg-neon/10 border border-neon/25 px-3 py-2.5">
                <StarIcon size={16} weight="fill" className="text-neon mt-0.5 shrink-0" />
                <p className="text-xs text-white leading-relaxed">
                  Create an account to <span className="text-neon font-semibold">{modal.reason}</span> — it syncs to every device you log in from.
                </p>
              </div>
            )}
            <div className="flex border border-white/10">
              <button data-testid="auth-tab-login" onClick={() => setTab("login")} className={`flex-1 py-2.5 text-sm font-semibold transition-colors ${tab === "login" ? "bg-neon/10 text-neon" : "text-muted-foreground hover:text-white"}`}>Log in</button>
              <button data-testid="auth-tab-signup" onClick={() => setTab("signup")} className={`flex-1 py-2.5 text-sm font-semibold transition-colors ${tab === "signup" ? "bg-neon/10 text-neon" : "text-muted-foreground hover:text-white"}`}>Sign up</button>
            </div>
          </>
        )}

        {isVerify ? (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">Enter the 6-digit code we sent to <span className="text-white font-mono">{email}</span>.</p>
            <input data-testid="auth-code-input" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="123456" inputMode="numeric" className="w-full bg-black/40 border border-white/10 px-4 py-3 text-center text-2xl font-mono tracking-[0.4em] focus:outline-none focus:border-neon/50" />
            <button data-testid="auth-resend-btn" onClick={async () => { try { await resendCode(email); toast.success("New code sent"); } catch (e) { toast.error(errMsg(e)); } }} className="text-xs text-neon hover:underline">Resend code</button>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center gap-2 bg-black/40 border border-white/10 px-3 focus-within:border-neon/50">
              <EnvelopeSimpleIcon size={16} className="text-muted-foreground" />
              <input data-testid="auth-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@email.com" className="flex-1 bg-transparent py-3 text-sm focus:outline-none" />
            </div>
            <div className="flex items-center gap-2 bg-black/40 border border-white/10 px-3 focus-within:border-neon/50">
              <LockKeyIcon size={16} className="text-muted-foreground" />
              <input data-testid="auth-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} onKeyDown={(e) => e.key === "Enter" && submit()} placeholder="Password (6+ chars)" className="flex-1 bg-transparent py-3 text-sm focus:outline-none" />
            </div>
          </div>
        )}

        <button data-testid="auth-submit-btn" onClick={submit} disabled={busy} className="w-full flex items-center justify-center gap-2 py-3 bg-neon text-black font-semibold text-sm hover:bg-neon/90 transition-colors disabled:opacity-50">
          {busy && <SpinnerGapIcon size={16} className="animate-spin" />}
          {isVerify ? "Verify email" : tab === "signup" ? "Create account" : "Log in"}
        </button>

        <p className="text-[11px] text-muted-foreground text-center leading-relaxed">
          Your watchlist, alerts, tracked wallets & creators sync to your account across every device.
        </p>
      </div>
    </div>
  );
};
